import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatCurrency, formatPercent } from '../../utils/formatters';
import { API_BASE_URL } from '../../config';
import io from 'socket.io-client';
import './embed.css';

const EmbedTickerTape = () => {
  const [searchParams] = useSearchParams();

  // URL params for customization
  const theme = searchParams.get('theme') || 'dark';
  const bg = searchParams.get('bg') || (theme === 'dark' ? '#000000' : '#ffffff');
  const speed = searchParams.get('speed') || 'medium';
  const height = searchParams.get('height') || '50px';
  const showVolume = searchParams.get('volume') === 'true';
  const showChange = searchParams.get('change') !== 'false';
  const textColor = searchParams.get('color') || '#00ff41';
  const borderColor = searchParams.get('border') || '#00ff41';
  const refreshInterval = parseInt(searchParams.get('refresh')) || 10000;
  const direction = searchParams.get('direction') || 'left'; // left or right

  const [stocks, setStocks] = useState([]);
  const [isPaused, setIsPaused] = useState(false);
  const tickerRef = useRef(null);

  const speedConfig = {
    slow: 30,
    medium: 50,
    fast: 80
  };

  const fetchStocks = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/stocks`);
      if (response.ok) {
        const data = await response.json();
        setStocks(data);
      }
    } catch (error) {
      console.error('Error fetching stocks for ticker:', error);
    }
  };

  useEffect(() => {
    fetchStocks();

    // Socket for live price updates
    const socket = io(API_BASE_URL, { auth: { public: true } });
    socket.on('stock-updates', (updates) => {
      const stocksArray = Array.isArray(updates) ? updates : updates?.stocks;
      if (!stocksArray) return;
      setStocks(prev => {
        if (!prev || prev.length === 0) return prev;
        const priceMap = {};
        stocksArray.forEach(u => {
          if (u.ticker) priceMap[u.ticker.toUpperCase()] = parseFloat(u.currentPrice || u.price);
        });
        return prev.map(stock => {
          const livePrice = priceMap[stock.ticker?.toUpperCase()];
          if (livePrice && !isNaN(livePrice)) {
            return { ...stock, currentPrice: livePrice };
          }
          return stock;
        });
      });
    });

    const interval = setInterval(fetchStocks, refreshInterval);
    return () => {
      socket.disconnect();
      clearInterval(interval);
    };
  }, [refreshInterval]);

  const getAnimationDuration = () => {
    if (!tickerRef.current) return '60s';
    const contentWidth = tickerRef.current.scrollWidth;
    const pixelsPerSecond = speedConfig[speed] || speedConfig.medium;
    const duration = (contentWidth + window.innerWidth) / pixelsPerSecond;
    return `${duration}s`;
  };

  const formatStockDisplay = (stock) => {
    const changePercent = stock.basePrice
      ? ((stock.currentPrice - stock.basePrice) / stock.basePrice) * 100
      : 0;
    const isPositive = changePercent >= 0;

    return {
      ticker: stock.ticker,
      price: formatCurrency(stock.currentPrice),
      change: `${isPositive ? '+' : ''}${changePercent.toFixed(1)}%`,
      isPositive,
      volume: stock.volume
    };
  };

  const tickerContent = stocks.length > 0
    ? stocks.map(formatStockDisplay)
    : [{ ticker: 'LOADING', price: '...', change: '', isPositive: true }];

  const animationDuration = getAnimationDuration();
  const animationName = direction === 'right' ? 'scroll-right' : 'scroll-left';

  return (
    <div
      className={`embed-ticker-tape ${theme}`}
      style={{
        background: bg === 'transparent' ? 'transparent' : bg,
        height,
        borderTop: `1px solid ${borderColor}`,
        borderBottom: `1px solid ${borderColor}`,
      }}
      onClick={() => setIsPaused(!isPaused)}
    >
      <button className="embed-fullscreen-btn embed-fullscreen-corner" onClick={(e) => { e.stopPropagation(); document.documentElement.requestFullscreen?.(); }}>&#x26F6;</button>
      {/* Gradient fade edges */}
      <div className="ticker-fade-left" style={{ background: `linear-gradient(90deg, ${bg === 'transparent' ? '#000' : bg} 0%, transparent 100%)` }} />
      <div className="ticker-fade-right" style={{ background: `linear-gradient(90deg, transparent 0%, ${bg === 'transparent' ? '#000' : bg} 100%)` }} />

      <div
        ref={tickerRef}
        className="ticker-scroll-content"
        style={{
          animation: isPaused ? 'none' : `${animationName} ${animationDuration} linear infinite`,
        }}
      >
        {/* Triple content for seamless loop */}
        {[0, 1, 2].map((loopIndex) => (
          <div key={loopIndex} className="ticker-loop">
            {tickerContent.map((item, index) => (
              <React.Fragment key={`${loopIndex}-${index}`}>
                <span className="ticker-item" style={{ color: textColor }}>
                  <span className="ticker-symbol">{item.ticker}</span>
                  <span className="ticker-price">{item.price}</span>
                  {showChange && (
                    <span className={`ticker-change ${item.isPositive ? 'positive' : 'negative'}`}>
                      {item.change}
                    </span>
                  )}
                  {showVolume && item.volume && (
                    <span className="ticker-volume">Vol: {item.volume.toLocaleString()}</span>
                  )}
                </span>
                <span className="ticker-separator" style={{ color: `${textColor}50` }}>●</span>
              </React.Fragment>
            ))}
          </div>
        ))}
      </div>

      {isPaused && (
        <div className="ticker-paused-indicator">PAUSED</div>
      )}
    </div>
  );
};

export default EmbedTickerTape;
